import React, { useEffect, useState } from 'react'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import { useMediaQuery, useTheme } from '@material-ui/core'
import { useRouter } from 'next/router'

import { useQueryParamsFilterEmployers } from 'src/features/filters/common'
import {
  ProfileSpecialistCard,
  ProfileSpecialistSausageCard,
  useQueryProfileSpecialistList
} from '../common'
import { useProfileSpecialistListStyles } from './profile-specialist-list.styles'
import {
  AdvertisingRow,
  PaginationBlock,
  useQueryAdvertisement
} from 'src/common'
import { TranslationDictionary, useNormalizeAdsData } from '@plandi/common'
import { Skeleton } from '@material-ui/lab'

type ProfileSpecialistListProps = {
  changeCounter: (value: number) => void
  itemsView: string
  type: string
  translate?: TranslationDictionary['data']
  locale: string
}

export const ProfileSpecialistList: React.FC<ProfileSpecialistListProps> = (
  props
) => {
  const router = useRouter()
  const classes = useProfileSpecialistListStyles()
  const { isEmptyQueryFilter } = useQueryParamsFilterEmployers()

  const {
    data: profilesInfo,
    isLoading,
    mutate
  } = useQueryProfileSpecialistList(props.type)
  const {
    data: adsData,
    isLoading: adsLoading,
    isError: adsError
  } = useQueryAdvertisement()
  const { allAds } = useNormalizeAdsData(
    !adsLoading || !adsError ? adsData?.content : {}
  )

  const isCardView = props.itemsView === 'card'

  const theme = useTheme()

  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'sm'))
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [adsDataPurchases, setAdsDataPurchases] = useState(false)

  const cards = [1, 2, 3, 4, 5, 6, 7, 8, 9]

  useEffect(() => {
    if (profilesInfo) {
      props.changeCounter(profilesInfo.total ?? 0)
    }
  }, [profilesInfo, props])

  useEffect(() => {
    if (adsData) {
      setAdsDataPurchases(adsData.noAds ? adsData.noAds : false)
    }
  }, [adsData])

  useEffect(() => {
    if (props.type) mutate()
  }, [props.type, mutate, router])

  const handleChangePage = (pageNumber: number) => {
    router.push(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          page: pageNumber
        }
      },
      undefined,
      { shallow: false }
    )
  }

  if (isLoading || adsLoading) {
    return (
      <div className={isCardView ? classes.container : classes.columnContainer}>
        {cards.map((card) => (
          <Skeleton
            key={card}
            variant="rect"
            animation="wave"
            className={
              isCardView ? classes.skeletonCard : classes.skeletonCardColumn
            }
          />
        ))}
      </div>
    )
  }

  if (!profilesInfo?.data) {
    return (
      <Typography align="center" variant="h6">
        {props.translate?.['job-site']?.['no-specialist-resumes'] ??
          'На данный момент нет ни одного резюме специалиста'}
      </Typography>
    )
  }

  if (!profilesInfo?.data?.length && !isEmptyQueryFilter) {
    return (
      <Typography align="center" variant="h6">
        {props.translate?.['not-found-data'] ??
          'Ничего не найдено, попробуйте повторить поиск с другими параметрами'}
      </Typography>
    )
  }

  const CardComponent =
    !isCardView && isDesktop
      ? ProfileSpecialistSausageCard
      : ProfileSpecialistCard

  if (profilesInfo?.data.length < 15) {
    return (
      <>
        {isDesktop && (
          <>
            <div
              className={clsx({
                [classes.container]: isCardView || !isDesktop,
                [classes.columnContainer]: !isCardView && isDesktop
              })}
            >
              {profilesInfo?.data?.map((profile) => (
                <CardComponent
                  key={profile?.id}
                  {...profile}
                  translate={props.translate}
                  locale={props.locale}
                />
              ))}
            </div>
            {!adsDataPurchases && <AdvertisingRow data={allAds} />}
          </>
        )}
        {isTablet && (
          <>
            <div
              className={clsx({
                [classes.container]: isCardView || !isTablet,
                [classes.columnContainer]: !isCardView && isTablet
              })}
            >
              {profilesInfo?.data?.map((profile) => (
                <CardComponent
                  key={profile?.id}
                  {...profile}
                  translate={props.translate}
                  locale={props.locale}
                />
              ))}
            </div>
            {!adsDataPurchases && <AdvertisingRow data={allAds} />}
          </>
        )}
        {isMobile && (
          <>
            <div
              className={clsx({
                [classes.container]: isCardView || !isMobile,
                [classes.columnContainer]: !isCardView && isMobile
              })}
            >
              {profilesInfo?.data?.map((profile) => (
                <CardComponent
                  key={profile?.id}
                  {...profile}
                  translate={props.translate}
                  locale={props.locale}
                />
              ))}
            </div>
            {!adsDataPurchases && <AdvertisingRow data={allAds} />}
          </>
        )}
        <PaginationBlock
          className={classes.paginationBlock}
          handleChange={handleChangePage}
          page={Number(router.query.page || 1)}
          pageCount={profilesInfo.last_page}
        />
      </>
    )
  }

  return (
    <>
      {isDesktop && (
        <>
          <div
            className={clsx({
              [classes.container]: isCardView || !isDesktop,
              [classes.columnContainer]: !isCardView && isDesktop,
              [classes.noAds]:
                (!allAds?.length && !adsLoading) || adsDataPurchases
            })}
          >
            {profilesInfo?.data?.slice(0, 9).map((profile) => (
              <CardComponent
                key={profile?.id}
                {...profile}
                translate={props.translate}
                locale={props.locale}
              />
            ))}
          </div>
          {!adsDataPurchases && <AdvertisingRow data={allAds} />}
          <div
            className={clsx({
              [classes.container]: isCardView || !isDesktop,
              [classes.columnContainer]: !isCardView && isDesktop
            })}
          >
            {profilesInfo?.data?.slice(9, 18).map((profile) => (
              <CardComponent
                key={profile?.id}
                {...profile}
                translate={props.translate}
                locale={props.locale}
              />
            ))}
          </div>
          {!adsDataPurchases && <AdvertisingRow data={allAds} />}
        </>
      )}
      {isTablet && (
        <>
          <div
            className={clsx({
              [classes.container]: isCardView || !isTablet,
              [classes.columnContainer]: !isCardView && isTablet,
              [classes.noAds]: !allAds?.length && !adsLoading
            })}
            style={{ marginBottom: 15 }}
          >
            {profilesInfo?.data?.slice(0, 9).map((profile) => (
              <CardComponent
                key={profile?.id}
                {...profile}
                translate={props.translate}
                locale={props.locale}
              />
            ))}
          </div>
          <div
            className={clsx({
              [classes.container]: isCardView || !isTablet,
              [classes.columnContainer]: !isCardView && isTablet,
              [classes.noAds]: !allAds?.length && !adsLoading
            })}
          >
            {profilesInfo?.data?.slice(9, 18).map((profile) => (
              <CardComponent
                key={profile?.id}
                {...profile}
                translate={props.translate}
                locale={props.locale}
              />
            ))}
          </div>
          {!adsDataPurchases && <AdvertisingRow data={allAds} />}
          <div
            className={clsx({
              [classes.container]: isCardView || !isTablet,
              [classes.columnContainer]: !isCardView && isTablet
            })}
          >
            {profilesInfo?.data?.slice(18, 27).map((profile) => (
              <CardComponent
                key={profile?.id}
                {...profile}
                translate={props.translate}
                locale={props.locale}
              />
            ))}
          </div>
          {!adsDataPurchases && <AdvertisingRow data={allAds} />}
        </>
      )}
      {isMobile && (
        <>
          <div
            className={clsx({
              [classes.container]: isCardView || !isMobile,
              [classes.columnContainer]: !isCardView && isMobile,
              [classes.noAds]: !allAds?.length && !adsLoading
            })}
            style={{ marginBottom: 15 }}
          >
            {profilesInfo?.data?.slice(0, 6).map((profile) => (
              <CardComponent
                key={profile?.id}
                {...profile}
                translate={props.translate}
                locale={props.locale}
              />
            ))}
          </div>
          <div
            className={clsx({
              [classes.container]: isCardView || !isMobile,
              [classes.columnContainer]: !isCardView && isMobile,
              [classes.noAds]: !allAds?.length && !adsLoading
            })}
          >
            {profilesInfo?.data?.slice(6, 12).map((profile) => (
              <CardComponent
                key={profile?.id}
                {...profile}
                translate={props.translate}
                locale={props.locale}
              />
            ))}
          </div>
          {!adsDataPurchases && <AdvertisingRow data={allAds} />}
          <div
            className={clsx({
              [classes.container]: isCardView || !isMobile,
              [classes.columnContainer]: !isCardView && isMobile
            })}
          >
            {profilesInfo?.data?.slice(12, 18).map((profile) => (
              <CardComponent
                key={profile?.id}
                {...profile}
                translate={props.translate}
                locale={props.locale}
              />
            ))}
          </div>
          {!adsDataPurchases && <AdvertisingRow data={allAds} />}
        </>
      )}
      <PaginationBlock
        className={classes.paginationBlock}
        handleChange={handleChangePage}
        page={Number(router.query.page || 1)}
        pageCount={profilesInfo.last_page}
      />
    </>
  )
}
